import { useState, useEffect } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { Avatar } from "../common/Avatar";

// Skeleton Card
const ReviewCardSkeleton = ({ isMobile }) => (
  <div
    className="
      bg-white border border-[#E5E7EB]
      rounded-2xl p-4 sm:p-5 shadow-sm
      w-full max-w-[350px] sm:w-[350px] h-auto
      flex-shrink-0 animate-pulse
    "
  >
    {isMobile ? (
      <div className="flex flex-col items-center">
        <div className="w-16 h-16 rounded-full bg-gray-200 mb-3" />
        <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
        <div className="h-3 w-16 bg-gray-200 rounded mb-2" />
        <div className="flex gap-1 mb-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="w-4 h-4 bg-gray-200 rounded" />
          ))}
        </div>
        <div className="h-3 w-full bg-gray-200 rounded mb-1" />
        <div className="h-3 w-3/4 bg-gray-200 rounded mb-3" />
      </div>
    ) : (
      <div className="flex gap-3">
        <div className="w-12 h-12 rounded-full bg-gray-200" />
        <div className="flex-1">
          <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
          <div className="h-3 w-16 bg-gray-200 rounded mb-3" />
          <div className="flex gap-1 mb-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="w-4 h-4 bg-gray-200 rounded" />
            ))}
          </div>
          <div className="h-3 w-full bg-gray-200 rounded mb-1" />
          <div className="h-3 w-2/3 bg-gray-200 rounded" />
        </div>
      </div>
    )}
  </div>
);

export default function VendorCarouselCards({
  reviews = [],
  isLoading = false,
}) {
  const [index, setIndex] = useState(0);
  const [visibleCards, setVisibleCards] = useState(3);
  const [isMobile, setIsMobile] = useState(false);

  const total = reviews.length;

  // Screen size detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      setVisibleCards(mobile ? 1 : 3);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // UX rules
  const shouldEnableCarousel = total > visibleCards;
  const cardsToShow = shouldEnableCarousel ? visibleCards : total;

  const next = () => {
    if (!shouldEnableCarousel) return;
    setIndex((prev) => (prev + 1) % total);
  };

  const prev = () => {
    if (!shouldEnableCarousel) return;
    setIndex((prev) => (prev - 1 + total) % total);
  };

  const getVisibleReviews = () => {
    if (!shouldEnableCarousel) {
      return reviews.slice(0, cardsToShow);
    }

    const arr = [];
    for (let i = 0; i < cardsToShow; i++) {
      arr.push(reviews[(index + i) % total]);
    }
    return arr;
  };

  return (
    <div className="w-full py-8">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        Recent Customer Reviews
      </h2>

      <div className="flex items-center justify-center gap-4 w-full">
        {/* LEFT ARROW */}
        {shouldEnableCarousel && (
          <button
            onClick={prev}
            className="
              h-[260px] w-[45px]
              flex items-center justify-center
              border border-[#E5E7EB]
              rounded-lg bg-white
              hover:bg-gray-100 transition shadow-sm
            "
          >
            <FiChevronLeft className="w-6 h-6" />
          </button>
        )}

        {/* CARDS */}
        <div
          className="
            max-w-[1100px] w-full
            flex items-center justify-center
            gap-4 overflow-hidden
          "
        >
          {isLoading
            ? [...Array(cardsToShow)].map((_, i) => (
                <ReviewCardSkeleton key={i} isMobile={isMobile} />
              ))
            : getVisibleReviews().map((review, i) => (
                <div
                  key={i}
                  className="
                    bg-white border border-[#E5E7EB]
                    rounded-2xl p-4 sm:p-5 shadow-sm
                    w-full max-w-[350px]
                    flex-shrink-0
                  "
                >
                  {isMobile ? (
                    <div className="flex flex-col items-center text-center">
                      <Avatar
                        name={review?.name}
                        avatarUrl={review?.avatar}
                        size="lg"
                      />
                      <p className="font-semibold">{review.name}</p>
                      <p className="text-xs text-gray-500 mb-2">
                        {review.time}
                      </p>
                      <div className="text-yellow-500 mb-2">
                        {"★".repeat(review.rating)}
                        {"☆".repeat(5 - review.rating)}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        "{review.text}"
                      </p>
                      <p className="text-xs text-gray-500">
                        For: {review.experience}
                      </p>
                    </div>
                  ) : (
                    <div className="flex gap-3">
                      <Avatar
                        name={review?.name}
                        avatarUrl={review?.avatar}
                        size="lg"
                      />
                      <div className="flex-1">
                        <p className="font-semibold">{review.name}</p>
                        <p className="text-sm text-gray-500 mb-2">
                          {review.time}
                        </p>
                        <div className="text-yellow-500 mb-2">
                          {"★".repeat(review.rating)}
                          {"☆".repeat(5 - review.rating)}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          "{review.text}"
                        </p>
                        <p className="text-xs text-gray-500">
                          For: {review.experience}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
        </div>

        {/* RIGHT ARROW */}
        {shouldEnableCarousel && (
          <button
            onClick={next}
            className="
              h-[260px] w-[45px]
              flex items-center justify-center
              border border-[#E5E7EB]
              rounded-lg bg-white
              hover:bg-gray-100 transition shadow-sm
            "
          >
            <FiChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>
    </div>
  );
}
